import React, { useState, useMemo } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { NamedLink } from '../../../components';
import { buildCategorySlugMap, buildCategoryPath } from '../../../util/categoryHelpers';

import css from './MobileCategoryBar.module.css';

const MobileCategoryBar = ({ className, rootClassName }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const config = useConfiguration();

  const categories = config?.categoryConfiguration?.categories || [];

  // Only show if there are categories and we're on mobile
  if (!categories || categories.length === 0) {
    return null;
  }

  // Build slug map once
  const slugMap = useMemo(() => buildCategorySlugMap(categories), [categories]);

  const handleMainCategorySelect = (category) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;

    if (hasSubcategories) {
      // Trigger animation and show subcategories menu
      setIsAnimating(true);
      setTimeout(() => {
        setSelectedCategory(category);
        setIsAnimating(false);
      }, 150);
    } else {
      // For categories without subcategories, close menu (link will navigate)
      setIsExpanded(false);
      setSelectedCategory(null);
    }
  };

  const handleLinkClick = () => {
    // Close menu after navigation
    setIsExpanded(false);
    setSelectedCategory(null);
  };

  const handleBackToCategories = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setSelectedCategory(null);
      setIsAnimating(false);
    }, 150);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    // Reset selected category when closing
    if (isExpanded) {
      setSelectedCategory(null);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {/* Browse Categories Button */}
      <div className={css.categoryBar}>
        <button 
          className={css.expandButton}
          onClick={toggleExpanded}
          type="button"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Hide categories' : 'Browse categories'}
        >
          <span className={css.expandText}>
            Browse Categories
          </span>
          <span className={classNames(css.expandIcon, {
            [css.expandIconRotated]: isExpanded
          })}>
            ▼
          </span>
        </button>
      </div>

      {/* Expandable Menu */}
      <div className={classNames(css.categoryMenu, {
        [css.categoryMenuExpanded]: isExpanded
      })}>
        <div className={css.categoryContainer}>
          <div className={classNames(css.categoryListContainer, {
            [css.animating]: isAnimating
          })}>
            {!selectedCategory ? (
              // Main categories list
              <div className={css.categoryList}>
                {categories.map((category) => {
                  const hasSubcategories = category.subcategories && category.subcategories.length > 0;
                  const categoryPath = buildCategoryPath(slugMap, category.id);

                  if (hasSubcategories) {
                    // Render as button for categories with subcategories
                    return (
                      <button
                        key={category.id}
                        className={css.categoryButton}
                        onClick={() => handleMainCategorySelect(category)}
                        type="button"
                      >
                        {category.name}
                        <span className={css.categoryArrow}>›</span>
                      </button>
                    );
                  } else {
                    // Render as link for categories without subcategories
                    return (
                      <NamedLink
                        key={category.id}
                        name="CategoryPage"
                        params={{ category: categoryPath.category }}
                        className={css.categoryButton}
                        onClick={handleLinkClick}
                      >
                        {category.name}
                      </NamedLink>
                    );
                  }
                })}
              </div>
            ) : (
              // Subcategories list
              <div className={css.categoryList}>
                <button
                  className={css.backButton}
                  onClick={handleBackToCategories}
                  type="button"
                  aria-label="Back to categories"
                >
                  ‹
                </button>

                <NamedLink
                  name="CategoryPage"
                  params={{ category: buildCategoryPath(slugMap, selectedCategory.id).category }}
                  className={css.categoryButton}
                  onClick={handleLinkClick}
                >
                  All {selectedCategory.name}
                </NamedLink>

                {selectedCategory.subcategories.map((subcategory) => {
                  const subcategoryPath = buildCategoryPath(
                    slugMap,
                    selectedCategory.id,
                    subcategory.id
                  );

                  return (
                    <NamedLink
                      key={subcategory.id}
                      name="CategoryPageWithSubcategory"
                      params={{
                        category: subcategoryPath.category,
                        subcategory: subcategoryPath.subcategory
                      }}
                      className={css.subcategoryButton}
                      onClick={handleLinkClick}
                    >
                      {subcategory.name}
                    </NamedLink>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCategoryBar;